// The latest Feeder Flash episode, for the raisedbeef.com homepage rail.
//
// ⚠️ THIS DOES NOT TOUCH YOUTUBE, AND THAT IS THE WHOLE POINT.
// There is an older path — `useFeederFlashVideos` → the feeder-flash-videos
// edge function in its READ mode — which goes to YouTube on every invocation
// and is cached only at the edge. That is one YouTube request per visitor once
// a cache entry lapses, against a fixed daily quota: the "burns YouTube quota
// per device" bug on the known-bugs list. On a barn app that is a handful of
// people. On the front door of the company it would exhaust the quota and
// empty the tile for everyone.
//
// So the homepage reads a TABLE that one cron fills (the function's `sync`
// mode → feeder_flash_videos), through rpc_feeder_flash_latest, through
// /api/market's CDN. The RPC takes no parameters, so it is exactly one cache
// key for the entire internet.
//
// ⚠️ NO SUPABASE CLIENT. Sibling of rbHomeKpis.ts and rbCattleTicker.ts, bound
// by the same rule: this module is imported by a PRERENDERED document, the
// client touches localStorage at import and throws under Node, and
// tickerHomeWiring.test.ts fails the build if one appears in that tree.

import { rpc } from "@/lib/rbCattleTicker";

export interface FeederFlashEpisode {
  id: string;
  title: string;
  /** ISO timestamp, straight from YouTube. */
  publishedAt: string;
  thumbnailUrl: string;
  url: string;
  /** "Today", "Yesterday", or "Sep 23". Computed from the row, never the clock
   *  during render — the prerender and the browser must agree. */
  dayLabel: string;
}

/** Null rather than a placeholder: an absent episode renders no tile at all,
 *  which is honest. A card saying "no episode" is worse than no card. */
export const EMPTY_FEEDER_FLASH: FeederFlashEpisode | null = null;

/** Days between two dates in Central, by calendar day rather than by 24-hour
 *  blocks — an episode posted at 11pm is "yesterday" at 1am, not "today". */
function dayLabelFor(iso: string, now: Date): string {
  const ct = (d: Date) => d.toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
  const posted = new Date(iso);
  if (Number.isNaN(posted.getTime())) return "";
  const a = ct(posted);
  const b = ct(now);
  if (a === b) return "Today";
  const yest = new Date(now.getTime() - 86_400_000);
  if (a === ct(yest)) return "Yesterday";
  return posted.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "America/Chicago",
  });
}

export async function fetchFeederFlash(now: Date = new Date()): Promise<FeederFlashEpisode | null> {
  const rows = (await rpc<Record<string, unknown>>("rpc_feeder_flash_latest", {})) ?? [];
  const r = rows[0];
  if (!r) return null;

  const id = String(r.id ?? "");
  const url = String(r.url ?? "");
  const publishedAt = String(r.published_at ?? "");
  if (!id || !url || !publishedAt) return null;

  return {
    id,
    title: String(r.title ?? ""),
    publishedAt,
    thumbnailUrl: String(r.thumbnail_url ?? ""),
    url,
    dayLabel: dayLabelFor(publishedAt, now),
  };
}
